import type {
  ProcessDataStatusProfile,
  ProcessDataStatusProfileId,
  ProcessDataStatusRouteDef,
} from './process-data-status.types.js';

/** Tier 4 자연바다식품 — 메뉴 path 는 공통, processTypeCode·화면 메타만 JYBD 시드에 맞춤. */
export const JAYEON_BADA_PROCESS_DATA_STATUS_PROFILE: ProcessDataStatusProfile = {
  washing: {
    processTypeCode: 'WASH',
    screen: {
      title: '세척공정현황',
      description: '세척기 3기 세척수 온도(CCP)·세척시간·세척횟수 PLC 자동수집',
      primaryChartTitle: '세척수 온도 추이 (설비별)',
      primaryParamKey: 'water_temp_c',
      primaryChartKind: 'multi-equipment-line',
      primaryChartUnit: '℃',
      secondaryChartTitle: '세척시간·횟수',
      secondaryParamKey: 'wash_time_min',
      secondaryChartKind: 'single-line',
      secondaryChartUnit: 'min',
      kpiKind: 'wash',
      gridParamLabels: {
        water_temp_c: '세척수 온도',
        wash_time_min: '세척시간',
        wash_count: '세척횟수',
      },
    },
  },
  'broth-heating': {
    processTypeCode: 'BROTH_HEATING',
    screen: {
      title: '육수가열공정현황',
      description: '기존·신규 가열탱크 핵심온도(CCP) 및 가열시간 모니터링',
      primaryChartTitle: '가열온도 추이 (탱크별)',
      primaryParamKey: 'core_temp_c',
      primaryChartKind: 'multi-equipment-line',
      primaryChartUnit: '℃',
      secondaryChartTitle: '가열시간',
      secondaryParamKey: 'heat_time_min',
      secondaryChartKind: 'single-line',
      secondaryChartUnit: 'min',
      kpiKind: 'brine',
      gridParamLabels: {
        core_temp_c: '가열온도',
        heat_time_min: '가열시간',
      },
    },
  },
  'metal-detection': {
    processTypeCode: 'METAL_DETECT',
    screen: {
      title: '금속검출공정현황',
      description: '금속검출기 PASS/FAIL 이력·가동상태·가동시간',
      primaryChartTitle: '검출 결과 (PASS/FAIL)',
      primaryParamKey: 'metal_passed',
      primaryChartKind: 'pass-fail-bar',
      secondaryChartTitle: '설비 가동시간',
      secondaryParamKey: 'run_time_min',
      secondaryChartKind: 'single-line',
      secondaryChartUnit: 'min',
      kpiKind: 'metal',
      gridParamLabels: {
        metal_passed: '검출결과',
        inspect_history: '검사이력',
        run_time_min: '가동시간',
        run_status: '가동상태',
      },
    },
  },
  'inner-pack': {
    processTypeCode: 'INNER_PACKING',
    screen: {
      title: '내포장공정현황',
      description: '자동 포장설비 포장량·가동시간·가동상태',
      primaryChartTitle: '포장량 누적',
      primaryParamKey: 'pack_count',
      primaryChartKind: 'single-line',
      primaryChartUnit: 'Box',
      secondaryChartTitle: '가동상태 분포',
      secondaryParamKey: 'run_status',
      secondaryChartKind: 'status-bar',
      kpiKind: 'pack',
      gridParamLabels: {
        pack_count: '포장량',
        run_time_min: '가동시간',
        run_status: '가동상태',
      },
    },
  },
};

/** Tier 3 EGM 김치공장 — 동일 메뉴 슬롯, processTypeCode·파라미터는 kimchi-demo 시드. */
export const KIMCHI_PROCESS_DATA_STATUS_PROFILE: ProcessDataStatusProfile = {
  washing: {
    processTypeCode: 'K_WASH',
    screen: {
      title: '세척공정현황',
      description: '배추 버블세척기 세척수 온도(CCP)·유량·수압 PLC 자동수집',
      primaryChartTitle: '세척수 온도 추이',
      primaryParamKey: 'wash_temp_c',
      primaryChartKind: 'multi-equipment-line',
      primaryChartUnit: '℃',
      secondaryChartTitle: '세척수 유량',
      secondaryParamKey: 'wash_flow_lpm',
      secondaryChartKind: 'single-line',
      secondaryChartUnit: 'L/min',
      kpiKind: 'wash',
      gridParamLabels: {
        wash_temp_c: '세척수 온도',
        wash_flow_lpm: '세척수 유량',
        wash_pressure_bar: '수압',
        run_status: '가동상태',
      },
    },
  },
  'broth-heating': {
    processTypeCode: 'K_BRINE',
    screen: {
      title: '절임공정현황',
      description: '절임 사일로 온도·염도(CCP)·액위 모니터링',
      primaryChartTitle: '절임온도 추이',
      primaryParamKey: 'brine_temp_c',
      primaryChartKind: 'multi-equipment-line',
      primaryChartUnit: '℃',
      secondaryChartTitle: '염도',
      secondaryParamKey: 'salinity_pct',
      secondaryChartKind: 'single-line',
      secondaryChartUnit: '%',
      kpiKind: 'brine',
      gridParamLabels: {
        brine_temp_c: '절임온도',
        salinity_pct: '염도',
        brine_level_pct: '액위',
        cover_status: '덮개상태',
      },
    },
  },
  'metal-detection': {
    processTypeCode: 'K_METAL',
    screen: {
      title: '금속검출공정현황',
      description: '금속검출기 PASS/FAIL·검출횟수·감도',
      primaryChartTitle: '검출 결과 (PASS/FAIL)',
      primaryParamKey: 'metal_passed',
      primaryChartKind: 'pass-fail-bar',
      secondaryChartTitle: '검출횟수',
      secondaryParamKey: 'detect_count',
      secondaryChartKind: 'single-line',
      secondaryChartUnit: 'EA',
      kpiKind: 'metal',
      gridParamLabels: {
        metal_passed: '통과상태',
        detect_count: '검출횟수',
        sensitivity_level: '감도레벨',
      },
    },
  },
  'inner-pack': {
    processTypeCode: 'K_PACK',
    screen: {
      title: '포장공정현황',
      description: '중량·진공 포장설비 중량(CCP)·포장수량·가동상태',
      primaryChartTitle: '포장수량 추이 (설비별)',
      primaryParamKey: 'pack_count',
      primaryChartKind: 'multi-equipment-line',
      primaryChartUnit: 'EA',
      secondaryChartTitle: '포장중량',
      secondaryParamKey: 'pack_weight_g',
      secondaryChartKind: 'single-line',
      secondaryChartUnit: 'g',
      kpiKind: 'pack',
      gridParamLabels: {
        pack_weight_g: '포장중량',
        pack_count: '포장수량',
        reject_count: '중량불량',
        run_status: '가동상태',
      },
    },
  },
};

/**
 * Tier 4 (주)내츄럴코어 — 건강기능식품 액상·분말 제조.
 * 데이터현황 4화면(배합살균·여과·분말포장·충진포장). 파라미터 키는 「공정별 데이터 집계표」
 * = seedNaturalcoreMasterData(mes-be) ProcessParameter.key 와 일치.
 * (공정관리 온도알림은 별도 UI — NATURALCORE_TEMP_ALERT_CONFIGS + ProcessTempAlertPage)
 */
export const NATURALCORE_PROCESS_DATA_STATUS_PROFILE: ProcessDataStatusProfile = {
  'blend-steril': {
    processTypeCode: 'BLEND_STERIL',
    screen: {
      title: '배합살균 데이터 현황',
      description: '배합살균탱크 5기 살균온도(CCP)·살균시간·가동상태(PLC), 배합비율·투입량(태블릿)',
      primaryChartTitle: '살균온도 추이 (탱크별)',
      primaryParamKey: 'steril_temp_c',
      primaryChartKind: 'multi-equipment-line',
      primaryChartUnit: '℃',
      secondaryChartTitle: '살균시간',
      secondaryParamKey: 'steril_time_min',
      secondaryChartKind: 'single-line',
      secondaryChartUnit: 'min',
      kpiKind: 'brine',
      gridParamLabels: {
        steril_temp_c: '살균온도',
        steril_time_min: '살균시간',
        run_status: '설비가동상태',
        blend_ratio: '배합비율',
        input_qty_kg: '투입량',
      },
    },
  },
  filtration: {
    processTypeCode: 'FILTER',
    screen: {
      title: '여과 데이터 현황',
      description: '여과압력·온도·시간·여과망 규격/교체일시 (태블릿 수기)',
      primaryChartTitle: '여과압력 추이',
      primaryParamKey: 'filter_pressure_bar',
      primaryChartKind: 'single-line',
      primaryChartUnit: 'bar',
      secondaryChartTitle: '온도',
      secondaryParamKey: 'filter_temp_c',
      secondaryChartKind: 'single-line',
      secondaryChartUnit: '℃',
      kpiKind: 'pack',
      gridParamLabels: {
        filter_pressure_bar: '여과압력',
        filter_temp_c: '온도',
        filter_time_min: '시간',
        mesh_spec: '여과망 규격',
        mesh_changed_at: '여과망 교체일시',
      },
    },
  },
  'powder-pack': {
    processTypeCode: 'POWDER_PACK',
    screen: {
      title: '분말포장 데이터 현황',
      description: '분말포장라인 포장량·가동상태·가동시간 (PLC)',
      primaryChartTitle: '포장량 추이',
      primaryParamKey: 'pack_qty',
      primaryChartKind: 'single-line',
      primaryChartUnit: 'EA',
      secondaryChartTitle: '가동시간',
      secondaryParamKey: 'run_time_min',
      secondaryChartKind: 'single-line',
      secondaryChartUnit: 'min',
      kpiKind: 'pack',
      gridParamLabels: {
        pack_qty: '포장량',
        run_status: '설비가동상태',
        run_time_min: '가동시간',
      },
    },
  },
  'fill-pack': {
    processTypeCode: 'FILL_PACK',
    screen: {
      title: '충진포장 데이터 현황',
      description: '충진기 3기 충진시간·충진온도(CCP)·충진량·가동상태·가동시간 (PLC)',
      primaryChartTitle: '충진온도 추이 (충진기별)',
      primaryParamKey: 'fill_temp_c',
      primaryChartKind: 'multi-equipment-line',
      primaryChartUnit: '℃',
      secondaryChartTitle: '충진량',
      secondaryParamKey: 'fill_qty_ml',
      secondaryChartKind: 'single-line',
      secondaryChartUnit: 'mL',
      kpiKind: 'brine',
      gridParamLabels: {
        fill_time_min: '충진시간',
        fill_temp_c: '충진온도',
        fill_qty_ml: '충진량',
        run_status: '설비가동상태',
        run_time_min: '가동시간',
      },
    },
  },
};

/** jayeon-bada / kimchi 공용 4 공정 슬롯. */
export const PROCESS_DATA_STATUS_ROUTE_DEFS: ProcessDataStatusRouteDef[] = [
  {
    path: 'washing',
    name: 'mes.data-status.washing',
    menuCode: 'mes.data-status.washing',
    routeKey: 'washing',
  },
  {
    path: 'broth-heating',
    name: 'mes.data-status.broth-heating',
    menuCode: 'mes.data-status.broth-heating',
    routeKey: 'broth-heating',
  },
  {
    path: 'metal-detection',
    name: 'mes.data-status.metal-detection',
    menuCode: 'mes.data-status.metal-detection',
    routeKey: 'metal-detection',
  },
  {
    path: 'inner-pack',
    name: 'mes.data-status.inner-pack',
    menuCode: 'mes.data-status.inner-pack',
    routeKey: 'inner-pack',
  },
];

/**
 * naturalcore 데이터현황 route defs — 4 (mes.data-status.*).
 * path/menuCode 는 naturalcore 메뉴 시드(naturalcore-menus.ts) 와 일치해야 한다.
 */
export const NATURALCORE_PROCESS_DATA_STATUS_ROUTE_DEFS: ProcessDataStatusRouteDef[] = [
  {
    path: 'blend-steril',
    name: 'mes.data-status.blend-steril',
    menuCode: 'mes.data-status.blend-steril',
    routeKey: 'blend-steril',
  },
  {
    path: 'filtration',
    name: 'mes.data-status.filtration',
    menuCode: 'mes.data-status.filtration',
    routeKey: 'filtration',
  },
  {
    path: 'powder-pack',
    name: 'mes.data-status.powder-pack',
    menuCode: 'mes.data-status.powder-pack',
    routeKey: 'powder-pack',
  },
  {
    path: 'fill-pack',
    name: 'mes.data-status.fill-pack',
    menuCode: 'mes.data-status.fill-pack',
    routeKey: 'fill-pack',
  },
];

/**
 * 공정관리 온도알림 — 데이터현황과 완전히 다른 UI(ProcessTempAlertPage).
 * 설비별 현재 온도 카드 + 임계 상태 + 이탈 알림 이력. path/menuCode 는 메뉴 시드와 일치.
 */
export interface ProcessTempAlertConfig {
  path: string;
  name: string;
  menuCode: string;
  processTypeCode: string;
  tempParamKey: string;
  title: string;
  description: string;
  minValue: number;
  maxValue: number;
  equipmentLabel: string;
  /** 'gauge' = 설비별 반원 게이지 / 'timeseries' = 일별 추이 그래프(이탈·문열림 마커). */
  kind: 'gauge' | 'timeseries';
  /** timeseries 전용 — 문열림 이벤트 파라미터 key (있으면 그래프에 마커). */
  doorParamKey?: string;
}

export const NATURALCORE_TEMP_ALERT_CONFIGS: ProcessTempAlertConfig[] = [
  {
    path: 'fill-temp',
    name: 'mes.process-alert.fill-temp',
    menuCode: 'mes.process-alert.fill-temp',
    processTypeCode: 'FILL_PACK',
    tempParamKey: 'fill_temp_c',
    title: '충진 온도 알림',
    description: '충진기 3기 충진온도 임계 감시 (CCP 60~95℃)',
    minValue: 60,
    maxValue: 95,
    equipmentLabel: '충진기',
    kind: 'gauge',
  },
  {
    path: 'storage-temp',
    name: 'mes.process-alert.storage-temp',
    menuCode: 'mes.process-alert.storage-temp',
    processTypeCode: 'STORAGE',
    tempParamKey: 'storage_temp_c',
    title: '보관창고 온도 알림',
    description: '완제품 보관창고 일별 온도 추이 · 이탈/문열림 이벤트 (0~25℃)',
    minValue: 0,
    maxValue: 25,
    equipmentLabel: '보관창고',
    kind: 'timeseries',
    doorParamKey: 'door_open',
  },
];

/** chgfood 공정관리 — 품목별 유탕온도 알람(유탕기 게이지). */
export const CHGFOOD_TEMP_ALERT_CONFIGS: ProcessTempAlertConfig[] = [
  {
    path: 'fry-temp',
    name: 'mes.process-alert.fry-temp',
    menuCode: 'mes.process-alert.fry-temp',
    processTypeCode: 'FRYING',
    tempParamKey: 'fry_temp_c',
    title: '품목별 유탕온도 알람',
    description: '유탕기 3대 유탕온도 임계 감시 (품목 기준 160~180℃)',
    minValue: 160,
    maxValue: 180,
    equipmentLabel: '유탕기',
    kind: 'gauge',
  },
];

/**
 * Tier 4 대한폴리켐(polychem) — FMB 배합공정 특화 (designs/ai-polychem-project-setup.md).
 * 니더기 부하전류·전력·RPM + 내부온도·냉각수 입/출구 온도. 파라미터 키는
 * 배합 설비 센서(ProcessParameter.key) 등록 시 아래 키와 일치시켜야 한다.
 */
export const POLYCHEM_PROCESS_DATA_STATUS_PROFILE: ProcessDataStatusProfile = {
  mixing: {
    processTypeCode: 'FMB_MIXING',
    screen: {
      title: '배합 데이터 현황',
      description: '니더기 부하 전류·전력량·RPM (PLC/암페어미터)',
      primaryChartTitle: '부하 전류 추이 (니더기)',
      primaryParamKey: 'current_a',
      primaryChartKind: 'multi-equipment-line',
      primaryChartUnit: 'A',
      secondaryChartTitle: '전력량',
      secondaryParamKey: 'power_kw',
      secondaryChartKind: 'single-line',
      secondaryChartUnit: 'kW',
      kpiKind: 'pack',
      gridParamLabels: {
        current_a: '부하전류',
        power_kw: '전력량',
        rpm: 'RPM',
        run_status: '설비가동상태',
        run_time_min: '가동시간',
      },
    },
  },
  'mixing-temp': {
    processTypeCode: 'FMB_MIXING',
    screen: {
      title: '배합 온도 현황',
      description: '니더기 내부온도·냉각수 입/출구 온도 (온도센서)',
      primaryChartTitle: '내부온도 추이 (니더기)',
      primaryParamKey: 'temp_internal_c',
      primaryChartKind: 'multi-equipment-line',
      primaryChartUnit: '℃',
      secondaryChartTitle: '냉각수 출구온도',
      secondaryParamKey: 'coolant_out_c',
      secondaryChartKind: 'single-line',
      secondaryChartUnit: '℃',
      kpiKind: 'brine',
      gridParamLabels: {
        temp_internal_c: '내부온도',
        coolant_in_c: '냉각수 입구온도',
        coolant_out_c: '냉각수 출구온도',
        run_status: '설비가동상태',
      },
    },
  },
};

/** polychem 데이터현황 route defs — path/menuCode 는 tier4 polychem-menus.ts 시드와 일치. */
export const POLYCHEM_PROCESS_DATA_STATUS_ROUTE_DEFS: ProcessDataStatusRouteDef[] = [
  {
    path: 'mixing',
    name: 'mes.data-status.mixing',
    menuCode: 'mes.data-status.mixing',
    routeKey: 'mixing',
  },
  {
    path: 'mixing-temp',
    name: 'mes.data-status.mixing-temp',
    menuCode: 'mes.data-status.mixing-temp',
    routeKey: 'mixing-temp',
  },
];

/** polychem 공정관리 온도알림 — 니더기 내부온도 임계 감시 (공정표준 확정 전 임시 135~165℃). */
export const POLYCHEM_TEMP_ALERT_CONFIGS: ProcessTempAlertConfig[] = [
  {
    path: 'mixing-temp-alert',
    name: 'mes.process-alert.mixing-temp',
    menuCode: 'mes.process-alert.mixing-temp',
    processTypeCode: 'FMB_MIXING',
    tempParamKey: 'temp_internal_c',
    title: '배합 온도 알림',
    description: '니더기 내부온도 임계 감시 (임시 135~165℃ — 공정표준 확정 시 갱신)',
    minValue: 135,
    maxValue: 165,
    equipmentLabel: '니더기',
    kind: 'gauge',
  },
];

const TEMP_ALERT_MAP: Partial<Record<ProcessDataStatusProfileId, ProcessTempAlertConfig[]>> = {
  naturalcore: NATURALCORE_TEMP_ALERT_CONFIGS,
  chgfood: CHGFOOD_TEMP_ALERT_CONFIGS,
  polychem: POLYCHEM_TEMP_ALERT_CONFIGS,
};

export function getProcessTempAlertConfigs(
  profileId: ProcessDataStatusProfileId,
): ProcessTempAlertConfig[] {
  return TEMP_ALERT_MAP[profileId] ?? [];
}

/**
 * Tier 4 춘향골식품(chgfood) — 도포·건조 → 유탕 → 금속검출 → 포장 + 냉장/냉동 창고.
 * 데이터현황 5화면(도포건조·유탕·금속검출·냉동·냉장). 파라미터 키는 「공정별 데이터 집계표」
 * = seedChgfoodMasterData(mes-be) ProcessParameter.key 와 일치.
 */
export const CHGFOOD_PROCESS_DATA_STATUS_PROFILE: ProcessDataStatusProfile = {
  'coating-dry': {
    processTypeCode: 'COATING_DRY',
    screen: {
      title: '도포·건조공정 데이터 현황',
      description: '건조기 온습도센서 19대 — 가동시간·온도·습도 (PLC)',
      primaryChartTitle: '건조기 온도 추이 (설비별)',
      primaryParamKey: 'dry_temp_c',
      primaryChartKind: 'multi-equipment-line',
      primaryChartUnit: '℃',
      secondaryChartTitle: '건조기 습도',
      secondaryParamKey: 'dry_humidity',
      secondaryChartKind: 'single-line',
      secondaryChartUnit: '%',
      kpiKind: 'brine',
      gridParamLabels: {
        dry_run_time: '가동시간',
        dry_temp_c: '온도',
        dry_humidity: '습도',
      },
    },
  },
  frying: {
    processTypeCode: 'FRYING',
    screen: {
      title: '유탕공정 데이터 현황',
      description: '유탕기 3대 — 유탕 온도(CCP)·조리 시간 (PLC)',
      primaryChartTitle: '유탕 온도 추이 (유탕기별)',
      primaryParamKey: 'fry_temp_c',
      primaryChartKind: 'multi-equipment-line',
      primaryChartUnit: '℃',
      secondaryChartTitle: '조리 시간',
      secondaryParamKey: 'cook_time_min',
      secondaryChartKind: 'single-line',
      secondaryChartUnit: 'min',
      kpiKind: 'brine',
      gridParamLabels: {
        fry_temp_c: '유탕 온도',
        cook_time_min: '조리 시간',
      },
    },
  },
  'metal-detect': {
    processTypeCode: 'METAL_DETECT',
    screen: {
      title: '금속검출공정 데이터 현황',
      description: '금속검출기 2대 — 불량품·정상제품 수량 (PLC)',
      primaryChartTitle: '정상제품 수량 추이',
      primaryParamKey: 'normal_qty',
      primaryChartKind: 'single-line',
      primaryChartUnit: 'EA',
      secondaryChartTitle: '불량품 수량',
      secondaryParamKey: 'defect_qty',
      secondaryChartKind: 'single-line',
      secondaryChartUnit: 'EA',
      kpiKind: 'pack',
      gridParamLabels: {
        defect_qty: '불량품 수량',
        normal_qty: '정상제품 수량',
      },
    },
  },
  freezer: {
    processTypeCode: 'FREEZER',
    screen: {
      title: '냉동공정 데이터 현황',
      description: '냉동창고 설비 온도(PV/SV)·가동시간 (PLC)',
      primaryChartTitle: '냉동 온도(PV) 추이',
      primaryParamKey: 'freezer_temp_pv',
      primaryChartKind: 'multi-equipment-line',
      primaryChartUnit: '℃',
      secondaryChartTitle: '설비 가동시간',
      secondaryParamKey: 'freezer_run_time',
      secondaryChartKind: 'single-line',
      secondaryChartUnit: 'min',
      kpiKind: 'brine',
      gridParamLabels: {
        freezer_temp_pv: '설비 온도(PV)',
        freezer_temp_sv: '설비 온도(SV)',
        freezer_run_time: '가동시간',
      },
    },
  },
  fridge: {
    processTypeCode: 'FRIDGE',
    screen: {
      title: '냉장공정 데이터 현황',
      description: '냉장창고 설비 온도(PV/SV)·가동시간 (PLC)',
      primaryChartTitle: '냉장 온도(PV) 추이',
      primaryParamKey: 'fridge_temp_pv',
      primaryChartKind: 'multi-equipment-line',
      primaryChartUnit: '℃',
      secondaryChartTitle: '설비 가동시간',
      secondaryParamKey: 'fridge_run_time',
      secondaryChartKind: 'single-line',
      secondaryChartUnit: 'min',
      kpiKind: 'brine',
      gridParamLabels: {
        fridge_temp_pv: '설비 온도(PV)',
        fridge_temp_sv: '설비 온도(SV)',
        fridge_run_time: '가동시간',
      },
    },
  },
};

/** chgfood 데이터현황 route defs — 5 (mes.data-status.*). */
export const CHGFOOD_PROCESS_DATA_STATUS_ROUTE_DEFS: ProcessDataStatusRouteDef[] = [
  {
    path: 'coating-dry',
    name: 'mes.data-status.coating-dry',
    menuCode: 'mes.data-status.coating-dry',
    routeKey: 'coating-dry',
  },
  {
    path: 'frying',
    name: 'mes.data-status.frying',
    menuCode: 'mes.data-status.frying',
    routeKey: 'frying',
  },
  {
    path: 'metal-detect',
    name: 'mes.data-status.metal-detect',
    menuCode: 'mes.data-status.metal-detect',
    routeKey: 'metal-detect',
  },
  {
    path: 'freezer',
    name: 'mes.data-status.freezer',
    menuCode: 'mes.data-status.freezer',
    routeKey: 'freezer',
  },
  {
    path: 'fridge',
    name: 'mes.data-status.fridge',
    menuCode: 'mes.data-status.fridge',
    routeKey: 'fridge',
  },
];

/**
 * Tier 4 구포국수(gupo) — 국수 제조 (L.MES.GUPO.8 §4.2.7).
 * 데이터현황 4화면(염수탱크·사일로·건조실·금속검출). processTypeCode·파라미터 키는
 * mes-be `seed-catalog/gupo-master-data.ts`(GUPO_PROCESSES ProcessParameter.key)와
 * 일치해야 한다 — 염수탱크·염수온도는 반죽배합(DOUGH_MIXING), 사일로 수위는
 * 원재료입고(RECEIVING), 금속검출(CCP)은 내포장(INNER_PACKING) 공정 소속.
 * gridParamLabels 라벨은 시드 파라미터/센서 명칭과 맞춘다(수기입력 키 매핑에도 쓰인다).
 */
export const GUPO_PROCESS_DATA_STATUS_PROFILE: ProcessDataStatusProfile = {
  'brine-tank': {
    processTypeCode: 'DOUGH_MIXING',
    screen: {
      title: '염수탱크 데이터 현황',
      description: '반죽배합 염수탱크 수위·염수 온도 모니터링',
      primaryChartTitle: '염수 수위 추이',
      primaryParamKey: 'brine_level_pct',
      primaryChartKind: 'single-line',
      primaryChartUnit: '%',
      secondaryChartTitle: '염수 온도',
      secondaryParamKey: 'brine_temp_c',
      secondaryChartKind: 'single-line',
      secondaryChartUnit: '℃',
      kpiKind: 'brine',
      gridParamLabels: {
        brine_level_pct: '염수 수위',
        brine_temp_c: '염수 온도',
      },
    },
  },
  'flour-silo': {
    processTypeCode: 'RECEIVING',
    screen: {
      title: '사일로 데이터 현황',
      description: '밀가루 사일로 잔량(수위)·원재료 입고량 모니터링',
      primaryChartTitle: '밀가루 수위 추이',
      primaryParamKey: 'silo_flour_level_pct',
      primaryChartKind: 'single-line',
      primaryChartUnit: '%',
      secondaryChartTitle: '원재료 입고량',
      secondaryParamKey: 'receipt_qty_kg',
      secondaryChartKind: 'single-line',
      secondaryChartUnit: 'kg',
      kpiKind: 'pack',
      gridParamLabels: {
        silo_flour_level_pct: '사일로 밀가루 수위',
        receipt_qty_kg: '원재료 입고량',
      },
    },
  },
  'drying-room': {
    processTypeCode: 'DRYING',
    screen: {
      title: '건조실 데이터 현황',
      description: '건조실 온도·습도·팬 회전수(RPM) 모니터링',
      primaryChartTitle: '건조실 온도 추이',
      primaryParamKey: 'dry_temp_c',
      primaryChartKind: 'single-line',
      primaryChartUnit: '℃',
      secondaryChartTitle: '건조실 습도',
      secondaryParamKey: 'dry_humidity_pct',
      secondaryChartKind: 'single-line',
      secondaryChartUnit: '%',
      kpiKind: 'brine',
      gridParamLabels: {
        dry_temp_c: '건조실 온도',
        dry_humidity_pct: '건조실 습도',
        dry_fan_rpm: '건조실 팬 회전수',
      },
    },
  },
  'metal-detection': {
    processTypeCode: 'INNER_PACKING',
    screen: {
      title: '금속검출 데이터 현황',
      description: '내포장 금속검출기 PASS/FAIL 검출 이력 (CCP)',
      primaryChartTitle: '검출 결과 (PASS/FAIL)',
      primaryParamKey: 'metal_passed',
      primaryChartKind: 'pass-fail-bar',
      kpiKind: 'metal',
      gridParamLabels: {
        metal_passed: '금속검출 결과',
      },
    },
  },
};

/** gupo 데이터현황 route defs — path/menuCode 는 gupo 메뉴 upsert 시드와 일치해야 한다. */
export const GUPO_PROCESS_DATA_STATUS_ROUTE_DEFS: ProcessDataStatusRouteDef[] = [
  {
    path: 'brine-tank',
    name: 'mes.data-status.brine-tank',
    menuCode: 'mes.data-status.brine-tank',
    routeKey: 'brine-tank',
  },
  {
    path: 'flour-silo',
    name: 'mes.data-status.flour-silo',
    menuCode: 'mes.data-status.flour-silo',
    routeKey: 'flour-silo',
  },
  {
    path: 'drying-room',
    name: 'mes.data-status.drying-room',
    menuCode: 'mes.data-status.drying-room',
    routeKey: 'drying-room',
  },
  {
    path: 'metal-detection',
    name: 'mes.data-status.metal-detection',
    menuCode: 'mes.data-status.metal-detection',
    routeKey: 'metal-detection',
  },
];

const PROFILE_MAP: Record<ProcessDataStatusProfileId, ProcessDataStatusProfile> = {
  'jayeon-bada': JAYEON_BADA_PROCESS_DATA_STATUS_PROFILE,
  kimchi: KIMCHI_PROCESS_DATA_STATUS_PROFILE,
  naturalcore: NATURALCORE_PROCESS_DATA_STATUS_PROFILE,
  chgfood: CHGFOOD_PROCESS_DATA_STATUS_PROFILE,
  polychem: POLYCHEM_PROCESS_DATA_STATUS_PROFILE,
  gupo: GUPO_PROCESS_DATA_STATUS_PROFILE,
};

const ROUTE_DEFS_MAP: Record<ProcessDataStatusProfileId, ProcessDataStatusRouteDef[]> = {
  'jayeon-bada': PROCESS_DATA_STATUS_ROUTE_DEFS,
  kimchi: PROCESS_DATA_STATUS_ROUTE_DEFS,
  naturalcore: NATURALCORE_PROCESS_DATA_STATUS_ROUTE_DEFS,
  chgfood: CHGFOOD_PROCESS_DATA_STATUS_ROUTE_DEFS,
  polychem: POLYCHEM_PROCESS_DATA_STATUS_ROUTE_DEFS,
  gupo: GUPO_PROCESS_DATA_STATUS_ROUTE_DEFS,
};

export function resolveProcessDataStatusProfile(
  profileId: ProcessDataStatusProfileId,
): ProcessDataStatusProfile {
  return PROFILE_MAP[profileId];
}

export function getProcessDataStatusRouteDefs(
  profileId: ProcessDataStatusProfileId,
): ProcessDataStatusRouteDef[] {
  return ROUTE_DEFS_MAP[profileId];
}
